import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Check, Trash2, RefreshCw, BookOpen, CheckCircle, BarChart3, MessageSquare, Lightbulb, Settings } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { fetchMathConcepts as fetchMathConceptsUtil } from '../../utils/mathConceptsUtils';
import { EditorComposer, Editor, Divider } from '../../editor';
import ToolbarPlugin from '../../editor/plugins/ToolbarPlugin/ToolbarPlugin';
import {
  AlignDropdown,
  BackgroundColorPicker,
  BlockFormatDropdown,
  BoldButton,
  CodeFormatButton,
  CodeLanguageDropdown,
  FloatingLinkEditor,
  FontFamilyDropdown,
  FontSizeDropdown,
  InsertDropdown,
  InsertLinkButton,
  ItalicButton,
  RedoButton,
  TextColorPicker,
  TextFormatDropdown,
  UnderlineButton,
  UndoButton,
} from '../../editor/plugins/ToolbarPlugin/components';
import { $createParagraphNode, $createTextNode, $getRoot } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createEquationNode } from '../../editor/nodes/EquationNode';
import { $createImageNode } from '../../editor/nodes/ImageNode';
import { createEditor } from 'lexical';
import { $generateHtmlFromNodes } from '@lexical/html';
import PlaygroundNodes from '../../editor/nodes/PlaygroundNodes';
import { processMathEquation, splitTextAndMath } from '../utils/latexParser';
import ProblemPreview from '../ProblemPreview';
import ProblemPreviewCards from '../ProblemPreviewCards';
import { Modal } from '../common/Modal';
import { MathceptDetailModal, useMathceptDetail } from '../common';
import katex from 'katex';
import 'katex/dist/katex.min.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

// EditorComponent - ProblemEditModal과 동일한 구조
function EditorComponent({ onLoad, onSave, editorKey }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // ContentEditable__root 요소를 찾아서 스타일 적용
    const editorElement = document.querySelector(`[data-editor-key="${editorKey}"] div.ContentEditable__root[contenteditable]`);
    if (editorElement) {
      editorElement.style.paddingLeft = '20px';
      editorElement.style.paddingRight = '20px';
      editorElement.style.paddingTop = '20px';
      editorElement.style.paddingBottom = '20px';
    }
  }, [editorKey]);

  // saveEditorState 함수 추가
  const saveEditorState = useCallback(() => {
    const editorState = editor.getEditorState();
    const json = editorState.toJSON();
    onSave(json);
  }, [editor, onSave]);

  const handleMathData = useCallback(async (data) => {
    try {
      console.log("Received data in handleMathData:", data);
      
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        
        let currentParagraph = $createParagraphNode();
        root.append(currentParagraph);

        // 데이터 구조 확인 및 수정
        const linedData = data.mathpix_data?.lined_data;
        if (!linedData) {
          console.error('No lined_data found in:', data);
          return;
        }

        linedData.forEach(item => {
          // chart나 diagram 타입 또는 text/math/table이 아닌 타입 처리
          if ((item.type !== 'text' && item.type !== 'math' && item.type !== 'table') || 
              item.type === 'chart' || 
              item.type === 'diagram') {
            // 이미지 데이터 처리
            if (item.content && item.content[0] && 
                (item.content[0].startsWith('data:image') || item.is_base64)) {
              const imageNode = $createImageNode({
                altText: 'Problem Image',
                src: item.content[0],
                width: item.cnt ? Math.abs(item.cnt[2][0] - item.cnt[0][0]) : undefined,
                height: item.cnt ? Math.abs(item.cnt[2][1] - item.cnt[0][1]) : undefined,
                maxWidth: 500,
                showCaption: false
              });
              currentParagraph.append(imageNode);
              
              // 이미지 다음에 새 단락 추가
              currentParagraph = $createParagraphNode();
              root.append(currentParagraph);
            }
          } else if (item.type === 'text') {
            item.content.forEach(text => {
              // 수식과 텍스트를 분리
              const mathSegments = splitTextAndMath(text);
              mathSegments.forEach(mathSegment => {
                if (mathSegment.type === 'text') {
                  // 일반 텍스트에서 (숫자) 패턴을 찾아 변환
                  const segments = mathSegment.content.split(/(\(\d+\))/);
                  segments.forEach(segment => {
                    const numberMatch = segment.match(/\((\d+)\)/);
                    if (numberMatch) {
                      const num = parseInt(numberMatch[1]);
                      if (num >= 1 && num <= 5) {
                        const circleNumber = ['①', '②', '③', '④', '⑤'][num - 1];
                        const equationNode = $createEquationNode(circleNumber, true);
                        currentParagraph.append(equationNode);
                      } else {
                        const textNode = $createTextNode(segment);
                        currentParagraph.append(textNode);
                      }
                    } else {
                      const textNode = $createTextNode(segment);
                      currentParagraph.append(textNode);
                    }
                  });
                } else { // math
                  const equation = processMathEquation(mathSegment.content);
                  const equationNode = $createEquationNode(equation, true);
                  currentParagraph.append(equationNode);
                }
              });
          });
          
          // 텍스트 다음에 새 단락 추가
          currentParagraph = $createParagraphNode();
          root.append(currentParagraph);
        } else if (item.type === 'math' || item.type === 'table') {
          // 기존 수식/테이블 처리 로직 유지
          const mathContent = item.content[0];
          let equation = mathContent.replace(/^\$\$(.*)\$\$/s, '$1');
          
          if (item.type === 'table') {
            equation = equation
              .replace(/\\begin\{tabular\}/g, '\\begin{array}')
              .replace(/\\end\{tabular\}/g, '\\end{array}')
              .replace(/\$/g, ' ')
              .replace(/\\hline(?!\s)/g, '\\hline ');
          }
          
          equation = processMathEquation(equation);
          const isDisplayMath = mathContent.startsWith('$$');
          const equationNode = $createEquationNode(equation, !isDisplayMath);
          currentParagraph.append(equationNode);
          
          currentParagraph = $createParagraphNode();
          root.append(currentParagraph);
        }
      });
    });
  } catch (error) {
    console.error('Math data handling error:', error);
    console.error('Error details:', error.message);
  }
}, [editor]);

  useEffect(() => {
    if (onLoad) {
      onLoad(handleMathData);
    }
  }, [onLoad, handleMathData]);

  return (
    <div data-editor-key={editorKey}>
      <div className="editor-container">
        <Editor>
          <ToolbarPlugin>
            <FontFamilyDropdown />
            <FontSizeDropdown />
            <Divider />
            <BoldButton />
            <ItalicButton />
            <UnderlineButton />
            <CodeFormatButton />
            <InsertLinkButton />
            <TextColorPicker />
            <BackgroundColorPicker />
            <TextFormatDropdown />
            <Divider />
            <InsertDropdown />
            <Divider />
            <AlignDropdown />
          </ToolbarPlugin>
        </Editor>
      </div>
      <div className="mt-4">
        <button
          onClick={saveEditorState}
          className="rounded-3xl bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
          disabled={!editor}
        >
          저장
        </button>
      </div>
    </div>
  );
}

// Lexical 데이터를 HTML로 변환하는 컴포넌트
function LexicalRenderer({ lexicalData }) {
  const [htmlContent, setHtmlContent] = useState('');

  useEffect(() => {
    if (!lexicalData || Object.keys(lexicalData).length === 0) {
      setHtmlContent('<p class="text-gray-500">내용이 없습니다.</p>');
      return;
    }

    if (!lexicalData.root) {
      setHtmlContent('<p class="text-gray-500">내용이 없습니다.</p>');
      return;
    }

    try {
      const editor = createEditor({
        nodes: [...PlaygroundNodes]
      });

      const processNode = (node) => {
        // node가 없거나 null인 경우 처리
        if (!node) {
          return null;
        }
        
        // 기본 속성 초기화
        node.style = node.style || "";
        node.format = node.format || "";
        node.indent = node.indent || 0;
        node.direction = node.direction || null;
        node.version = node.version || 1;

        if (node.type === 'equation') {
          node.equation = processMathEquation(node.equation);
        } else if (node.type === 'box-content') {
          node.className = 'PlaygroundEditorTheme__boxContent';
        }
        
        if (node.children && Array.isArray(node.children)) {
          node.children = node.children.map(child => processNode(child)).filter(child => child !== null);
        }
        return node;
      };

      const processedData = JSON.parse(JSON.stringify(lexicalData));
      const processedRoot = processNode(processedData.root);
      
      if (!processedRoot) {
        setHtmlContent('<p class="text-gray-500">내용이 없습니다.</p>');
        return;
      }
      
      processedData.root = processedRoot;
      
      const editorState = editor.parseEditorState(processedData);
      
      let content = '';
      editor.setEditorState(editorState);
      editor.update(() => {
        content = $generateHtmlFromNodes(editor);
      });

      setHtmlContent(content);
    } catch (error) {
      console.error('Error converting lexical to HTML:', error);
      setHtmlContent('<p class="text-red-500">내용을 렌더링하는데 실패했습니다.</p>');
    }
  }, [lexicalData]);

  return (
    <div 
      className="problem-content" 
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
}

function WorkDetail({ workId: workIdProp = null, onClose = null }) {
  // 임베드(ProblemHub) 시 workId prop 우선, 단독 라우트(/problem-agent/:work_id)면 URL 파라미터 사용
  const params = useParams();
  const work_id = workIdProp != null ? String(workIdProp) : params.work_id;
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessToken, handleApiError } = useAuth();
  const [mathConcepts, setMathConcepts] = useState([]);

  // URL에서 이전 페이지 정보와 필터 상태 추출
  const returnInfo = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const pageNum = params.get('page') || '1';
    const workId = params.get('work_id') || '';
    const workType = params.get('work_type') || '';
    const workStatus = params.get('work_status') || '';
    
    // 필터 query string 생성
    const filterParams = new URLSearchParams();
    if (workId) filterParams.set('work_id', workId);
    if (workType) filterParams.set('work_type', workType);
    if (workStatus) filterParams.set('work_status', workStatus);
    const filterQuery = filterParams.toString();
    
    return {
      pageNum,
      filterQuery,
      hasFilters: !!(workId || workType || workStatus)
    };
  }, [location.search]);

  // 목록으로 돌아가기 함수 (임베드 시 onClose로 위임)
  const navigateToList = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    const url = `/problem-agent/page/${returnInfo.pageNum}${returnInfo.filterQuery ? `?${returnInfo.filterQuery}` : ''}`;
    navigate(url);
  }, [navigate, returnInfo, onClose]);
  
  const [workData, setWorkData] = useState(null);
  const [matchedConcepts, setMatchedConcepts] = useState({});
  const [loading, setLoading] = useState(true);
  const [isEditingProblem, setIsEditingProblem] = useState(false);
  const [isEditingSolution, setIsEditingSolution] = useState(false);
  const [isEditingAnswer, setIsEditingAnswer] = useState(false);
  const [isEditingDifficulty, setIsEditingDifficulty] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [originalAnswer, setOriginalAnswer] = useState(''); // 기존 정답 데이터 (원본 문제)

  // 편집 상태값들
  const [editAnswer, setEditAnswer] = useState('');
  const [editDifficulty, setEditDifficulty] = useState(1);
  const [editPrompt, setEditPrompt] = useState('');
  
  // 문제 분석 옵션 상태 (work_type=3일 때만 사용)
  const [analysisOptions, setAnalysisOptions] = useState({
    solution: false,
    answer: false,
    concept_ids: false,
    difficulty: false
  });
  const [isEditingOptions, setIsEditingOptions] = useState(false);

  // 매스컨셉 상세 정보 모달 (공통 컴포넌트 사용)
  const { isModalOpen: mathceptDetailModalOpen, loading: mathceptDetailLoading, data: mathceptDetailData, fetchAndShow: fetchMathceptDetails, closeModal: closeMathceptDetailModal } = useMathceptDetail({ handleAuth: false });

  // 미리보기 모달 상태
  const [previewInfo, setPreviewInfo] = useState({
    isOpen: false,
    problemId: [],
    problemNumber: null,
    isCorrect: null
  });

  // 에디터 핸들러 참조
  const problemMathDataHandlerRef = useRef(null);
  const solutionMathDataHandlerRef = useRef(null);

  // fetchWithTokenRefresh 함수
  const fetchWithTokenRefresh = useCallback(async (url, options = {}) => {
    try {
      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': token,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.status === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      const data = await response.json();

      if (data.status_code === 401) {
        handleApiError(new Error('Invalid or expired token'));
        return null;
      }

      return data;
    } catch (error) {
      console.error('API 호출 에러:', error);
      if (error.message.includes('token') || error.message.includes('unauthorized')) {
        handleApiError(error);
      }
      throw error;
    }
  }, [getAccessToken, navigate, handleApiError]);

  // 작업 상세 정보 조회
  const fetchWorkDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`);
      
      if (!response) return;

      if (response.status === 'success') {
        setWorkData(response.data);
        // 편집 상태값 초기화 - 분석 결과 정답을 editAnswer에 설정
        setEditAnswer(response.data.work_result?.answer || '');
        setEditDifficulty(response.data.work_result?.difficulty || 1);
        
        // 프롬프트 편집 중이 아닐 때만 서버 데이터로 업데이트
        if (!isEditingPrompt) {
          setEditPrompt(response.data.user_prompt || '');
        }
        
        // 원본 문제에서 기존 정답 데이터 가져오기 (source_probs 필드에서)
        if (response.data.source_probs && response.data.source_probs.length > 0) {
          const sourceProblem = response.data.source_probs[0];
          const existingAnswer = sourceProblem.answer || '';
          setOriginalAnswer(existingAnswer); // 기존 정답을 originalAnswer에 설정
        }
        
        // work_type=3 (문제 분석)일 때 options 정보 설정
        if (response.data.work_type === 3 && response.data.options) {
          setAnalysisOptions({
            solution: response.data.options.solution || false,
            answer: response.data.options.answer || false,
            concept_ids: response.data.options.concept_ids || false,
            difficulty: response.data.options.difficulty || false
          });
        }
        
        // Math concepts 데이터 가져오기
        try {
          const concepts = await fetchMathConceptsUtil();
          setMathConcepts(concepts);
        } catch (error) {
          console.error('Error loading math concepts:', error);
        }
      } else {
        console.error('Failed to fetch work detail:', response);
        alert('작업 정보를 불러오는데 실패했습니다.');
        navigateToList();
      }
    } catch (error) {
      console.error('Error fetching work detail:', error);
      alert('작업 정보를 불러오는데 실패했습니다.');
      navigateToList();
    } finally {
      setLoading(false);
    }
  }, [work_id, fetchWithTokenRefresh, navigateToList, isEditingPrompt]);



  // LaTeX 수식 렌더링 함수
  const renderLatex = (text) => {
    if (!text) return '';
    try {
      // $...$ 패턴을 찾아서 LaTeX로 렌더링
      return text.replace(/\$([^$]+)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            throwOnError: false,
            displayMode: false
          });
        } catch (e) {
          return match; // 렌더링 실패 시 원본 반환
        }
      });
    } catch (error) {
      return text; // 전체 처리 실패 시 원본 반환
    }
  };

  // Math concepts 매칭 - workData와 mathConcepts가 모두 있을 때 실행
  useEffect(() => {
    if (workData?.work_result?.mathcepts && mathConcepts.length > 0) {
      const matched = {};
      
      Object.entries(workData.work_result.mathcepts).forEach(([id, conceptName]) => {
        const matchedConcept = mathConcepts.find(
          mc => mc.concept_name === conceptName || mc.text === conceptName
        );
        
        if (matchedConcept) {
          matched[id] = {
            concept_name: conceptName,
            order: matchedConcept.order,
            concept_type: matchedConcept.concept_type,
            math_type: matchedConcept.math_type,
            tags: matchedConcept.tags
          };
        } else {
          matched[id] = {
            concept_name: conceptName,
            order: null,
            concept_type: null,
            math_type: null,
            tags: null
          };
        }
      });
      
      setMatchedConcepts(matched);
    }
  }, [workData, mathConcepts]);

  // 에디터 상태 생성 함수들
  const createEditorState = useCallback((lexicalData) => {
    const editor = createEditor({
      nodes: [...PlaygroundNodes],
      theme: {
        paragraph: 'editor-paragraph',
        text: {
          bold: 'editor-text-bold',
          italic: 'editor-text-italic',
          underline: 'editor-text-underline',
        },
      }
    });
    
    if (lexicalData && typeof lexicalData === 'object') {
      try {
        const processedData = JSON.parse(JSON.stringify(lexicalData));
        
        if (!processedData.root) {
          return createEmptyEditorState(editor);
        }

        // 모든 노드에 대해 처리를 수행하는 재귀 함수
        const processNode = (node) => {
          // 기본 속성 초기화
          node.style = node.style || "";
          node.format = node.format || "";
          node.indent = node.indent || 0;
          node.direction = node.direction || null;
          node.version = node.version || 1;

          // equation 타입 노드 처리
          if (node.type === 'equation') {
            node.equation = processMathEquation(node.equation);
          }

          // children이 있는 경우 재귀적으로 처리
          if (node.children && Array.isArray(node.children)) {
            node.children = node.children.map(child => processNode(child));
          }

          return node;
        };

        // root 노드부터 시작하여 모든 노드 처리
        processedData.root = processNode(processedData.root);

        const editorState = editor.parseEditorState(processedData);
        return editorState;
        
      } catch (error) {
        console.error('Error parsing editor state:', error);
        return createEmptyEditorState(editor);
      }
    }
    
    return createEmptyEditorState(editor);
  }, []);

  // 빈 에디터 상태 생성 함수
  const createEmptyEditorState = (editor) => {
    return editor.parseEditorState({
      root: {
        children: [
          {
            children: [],
            direction: null,
            format: "",
            indent: 0,
            type: "paragraph",
            version: 1,
            style: ""
          }
        ],
        direction: null,
        format: "",
        indent: 0,
        type: "root",
        version: 1,
        style: ""
      }
    });
  };

  // 문제 에디터 초기 상태
  const getProblemInitialEditorState = useCallback(() => {
    if (workData?.work_result?.prob_lexical) {
      return createEditorState(workData.work_result.prob_lexical);
    }
    return createEmptyEditorState(createEditor({ nodes: [...PlaygroundNodes] }));
  }, [workData, createEditorState]);

  // 풀이 에디터 초기 상태
  const getSolutionInitialEditorState = useCallback(() => {
    if (workData?.work_result?.sol_lexical) {
      return createEditorState(workData.work_result.sol_lexical);
    }
    return createEmptyEditorState(createEditor({ nodes: [...PlaygroundNodes] }));
  }, [workData, createEditorState]);

  // 작업 상태 표시
  const getStatusDisplay = (status) => {
    if (status === 100 || status === 101) return { text: '대기 중', color: 'text-gray-500' };
    if (status === 150) return { text: '처리 중', color: 'text-primary' };
    if (status === 200) return { text: '완료', color: 'text-success' };
    if (status === 602) return { text: '특별 처리 대기', color: 'text-warning' };
    return { text: '실패', color: 'text-danger' };
  };

  // 작업 타입 표시
  const getWorkTypeDisplay = (type) => {
    if (type === 1) return '문제→문제';
    if (type === 2) return '개념→문제';
    if (type === 3) return '문제 분석';
    return '알 수 없음';
  };

  // 정답 전용 LaTeX 렌더링 함수
  const renderAnswerLatex = (text) => {
    try {
      // LaTeX 명령어 패턴 확인 (\begin, \frac, \sqrt 등)
      const latexPattern = /\\(begin|end|frac|sqrt|matrix|pmatrix|bmatrix|sum|int|prod|lim|alpha|beta|gamma|theta|lambda|mu|sigma|phi|psi|omega|Delta|Gamma|Lambda|Sigma|Phi|Psi|Omega)/;
      
      // 이미 $로 감싸져 있으면 그대로 처리
      if (text.includes('$')) {
        return renderLatex(text);
      }
      
      // LaTeX 명령어가 있으면 $$로 감싸서 처리
      if (latexPattern.test(text)) {
        const wrappedText = `$${text}$`;
        return renderLatex(wrappedText);
      }
      
      // 수학 기호나 변수가 있는지 확인
      const mathPattern = /[x-z]|[A-Z]|\^|\+|\-|\*|\/|=|≤|≥|<|>|∞|π|∑|∫|√|α|β|γ|θ|λ|μ|σ|φ|ψ|ω|\d+\s*[x-z]|[x-z]\s*\d+|\([^)]*[x-z][^)]*\)/i;
      
      if (mathPattern.test(text)) {
        // 수학 표현이 있으면 $$로 감싸서 LaTeX 처리
        const wrappedText = `$${text}$`;
        return renderLatex(wrappedText);
      }
      
      // 수학 표현이 없으면 일반 텍스트로 처리
      return text;
    } catch (error) {
      return text;
    }
  };

  // 문제 저장 핸들러
  const handleProblemSave = useCallback(async (lexicalJson) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          prob_lexical: lexicalJson
        })
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('문제가 저장되었습니다.');
        setIsEditingProblem(false);
        fetchWorkDetail(); // 데이터 새로고침
      } else {
        alert('문제 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving problem:', error);
      alert('문제 저장 중 오류가 발생했습니다.');
    }
  }, [work_id, fetchWithTokenRefresh, fetchWorkDetail]);

  // 풀이 저장 핸들러
  const handleSolutionSave = useCallback(async (lexicalJson) => {
    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          sol_lexical: lexicalJson
        })
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('풀이가 저장되었습니다.');
        setIsEditingSolution(false);
        fetchWorkDetail(); // 데이터 새로고침
      } else {
        alert('풀이 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving solution:', error);
      alert('풀이 저장 중 오류가 발생했습니다.');
    }
  }, [work_id, fetchWithTokenRefresh, fetchWorkDetail]);

  // 정답 저장 핸들러
  const handleAnswerSave = useCallback(async () => {
    if (!editAnswer.trim()) {
      alert('정답을 입력해주세요.');
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          answer: editAnswer.trim()
        })
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('정답이 저장되었습니다.');
        setIsEditingAnswer(false);
        fetchWorkDetail(); // 데이터 새로고침
      } else {
        alert('정답 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving answer:', error);
      alert('정답 저장 중 오류가 발생했습니다.');
    }
  }, [work_id, editAnswer, fetchWithTokenRefresh, fetchWorkDetail]);

  // 난이도 저장 핸들러
  const handleDifficultySave = useCallback(async () => {
    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          difficulty: editDifficulty
        })
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('난이도가 저장되었습니다.');
        setIsEditingDifficulty(false);
        fetchWorkDetail(); // 데이터 새로고침
      } else {
        alert('난이도 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving difficulty:', error);
      alert('난이도 저장 중 오류가 발생했습니다.');
    }
  }, [work_id, editDifficulty, fetchWithTokenRefresh, fetchWorkDetail]);

  // 분석 옵션 저장 핸들러 (work_type=3일 때만)
  const handleOptionsSave = useCallback(async () => {
    const selectedOptions = Object.keys(analysisOptions).filter(key => analysisOptions[key]);
    
    if (selectedOptions.length === 0) {
      alert('최소 하나 이상의 분석 옵션을 선택해주세요.');
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          options: selectedOptions
        })
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('분석 옵션이 저장되었습니다.');
        setIsEditingOptions(false);
        fetchWorkDetail(); // 데이터 새로고침
      } else {
        alert('분석 옵션 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error saving analysis options:', error);
      alert('분석 옵션 저장 중 오류가 발생했습니다.');
    }
  }, [work_id, analysisOptions, fetchWithTokenRefresh, fetchWorkDetail]);

  // 프롬프트 변경 후 재시도 핸들러
  const handlePromptRetry = useCallback(async () => {
    if (!editPrompt.trim()) {
      alert('AI 지시사항을 입력해주세요.');
      return;
    }

    if (editPrompt.trim() === workData.user_prompt) {
      // 프롬프트가 동일하면 바로 재시도
      if (!window.confirm('프롬프트가 변경되지 않았습니다. 그대로 재시도하시겠습니까?')) {
        return;
      }
      
      try {
        const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}/retry`, {
          method: 'POST'
        });

        if (!response) return;

        if (response.status === 'success') {
          alert('작업이 재시도 큐에 추가되었습니다.');
          setIsEditingPrompt(false);
          
          // 약간의 지연 후 데이터 새로고침
          setTimeout(() => {
            fetchWorkDetail();
          }, 300);
        } else {
          alert('작업 재시도에 실패했습니다.');
        }
      } catch (error) {
        console.error('Error retrying work:', error);
        alert('작업 재시도 중 오류가 발생했습니다.');
      }
      return;
    }

    if (!window.confirm('AI 지시사항을 변경하고 재시도하시겠습니까?')) {
      return;
    }

    try {
      // 1단계: 프롬프트 업데이트
      const updateResponse = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          user_prompt: editPrompt.trim()
        })
      });

      if (!updateResponse || updateResponse.status !== 'success') {
        alert('프롬프트 업데이트에 실패했습니다.');
        return;
      }

      // 2단계: 재시도 요청
      const retryResponse = await fetchWithTokenRefresh(`/api/probagent/${work_id}/retry`, {
        method: 'POST'
      });

      if (!retryResponse) return;

      if (retryResponse.status === 'success') {
        alert('프롬프트가 업데이트되고 작업이 재시도 큐에 추가되었습니다.');
        
        // 로컬 상태를 즉시 업데이트하여 변경된 프롬프트를 반영
        setWorkData(prevData => ({
          ...prevData,
          user_prompt: editPrompt.trim()
        }));
        
        setIsEditingPrompt(false);
        
        // fetchWorkDetail()을 약간 지연시켜 백엔드 업데이트 완료 후 호출
        setTimeout(() => {
          fetchWorkDetail();
        }, 500); // 0.5초 지연
      } else {
        alert('작업 재시도에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error updating prompt and retrying:', error);
      alert('프롬프트 업데이트 및 재시도 중 오류가 발생했습니다.');
    }
  }, [work_id, editPrompt, workData?.user_prompt, fetchWithTokenRefresh, fetchWorkDetail]);

  // 작업 완료 처리 핸들러
  const handleComplete = useCallback(async () => {
    if (!workData.work_result) {
      alert('완료할 수 있는 작업 결과가 없습니다.');
      return;
    }

    if (!window.confirm('작업을 완료 처리하여 문제를 생성하시겠습니까?\n\n이 작업은 되돌릴 수 없습니다.')) {
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}`, {
        method: 'POST'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('문제가 성공적으로 생성되었습니다.');
        navigateToList();
      } else {
        alert('문제 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error completing work:', error);
      alert('문제 생성 중 오류가 발생했습니다.');
    }
  }, [work_id, workData?.work_result, fetchWithTokenRefresh, navigateToList]);

  // 재시도 핸들러
  const handleRetry = useCallback(async () => {
    if (!window.confirm('작업을 재시도하시겠습니까?')) {
      return;
    }

    try {
      const response = await fetchWithTokenRefresh(`/api/probagent/${work_id}/retry`, {
        method: 'POST'
      });

      if (!response) return;

      if (response.status === 'success') {
        alert('작업이 재시도 큐에 추가되었습니다.');
        fetchWorkDetail();
      } else {
        alert('작업 재시도에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error retrying work:', error);
      alert('작업 재시도 중 오류가 발생했습니다.');
    }
  }, [work_id, fetchWithTokenRefresh, fetchWorkDetail]);

  // 에디터 핸들러 설정
  const setProblemMathDataHandler = useCallback((handler) => {
    problemMathDataHandlerRef.current = handler;
  }, []);

  const setSolutionMathDataHandler = useCallback((handler) => {
    solutionMathDataHandlerRef.current = handler;
  }, []);

  // 원본 문제 미리보기 핸들러
  const handleOriginalPreview = (problemIds) => {
    setPreviewInfo({
      isOpen: true,
      problemId: problemIds,
      problemNumber: null,
      isCorrect: null
    });
  };

  // 초기 데이터 로드
  useEffect(() => {
    if (work_id) {
      fetchWorkDetail();
    }
  }, [work_id, fetchWorkDetail]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="size-8 animate-spin rounded-full border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!workData) {
    return (
      <div className="py-8 text-center">
        <p className="mb-4 text-gray-500">작업을 찾을 수 없습니다.</p>
        <button
          onClick={navigateToList}
          className="rounded-3xl bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
        >
          목록으로 돌아가기
        </button>
      </div>
    );
  }

  const statusInfo = getStatusDisplay(workData.status);
  // 작업 완료 가능 여부 확인
  const canComplete = workData.status === 200 && workData.work_result && !workData.is_wip;
  // 재시도 가능 여부 확인
  const canRetry = workData.status !== 150 && workData.status !== 100 && workData.status !== 101 && !workData.is_wip;

  return (
    <div className="space-y-4">
      {/* 작업 정보 헤더 */}
      <div className="relative rounded-3xl border bg-white p-[14.4px] shadow-lg border-gray-200">
        {/* 카드 헤더 */}
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div 
              className="text-2xl font-bold self-center"
              style={{ 
                color: statusInfo.text === '완료' ? '#4ABC85' : 
                       statusInfo.text === '실패' ? '#F9344B' : '#000000'
              }}
            >
              {workData.work_id}
            </div>
            
            <div className="flex items-center gap-2 self-center">
              <span className="text-sm text-black font-bold">{getWorkTypeDisplay(workData.work_type)}</span>
              <span className="text-xs text-gray-600">({workData.author})</span>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* 상태 표시 */}
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${
              statusInfo.text === '완료' ? 'bg-[#4ABC85]' :
              statusInfo.text === '처리 중' ? 'bg-blue-500' :
              statusInfo.text === '대기 중' ? 'bg-gray-500' :
              statusInfo.text === '특별 처리 대기' ? 'bg-orange-500' :
              'bg-red-500'
            }`}>
              {statusInfo.text}
            </span>
          </div>
        </div>
        
        {/* AI 지시사항 */}
        <div className="relative rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 mt-2">
          <div className="space-y-1 text-sm">
            <div className="font-medium">AI 지시사항:</div>
            <div className="break-words">{workData.user_prompt}</div>
          </div>
        </div>
        
        {/* 추가 정보 */}
        <div className="relative rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-3 mt-2">
          <div className="flex justify-between text-sm">
            <div><span className="font-medium">시도 횟수:</span> {workData.attempt_count}회</div>
            <div><span className="font-medium">생성일:</span> {new Date(workData.created_at).toLocaleDateString()}</div>
          </div>
        </div>
      </div>

      {/* 원본 정보 섹션 */}
      <div className="rounded-3xl bg-white p-4 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold">📋 원본 정보</h2>
        
        {(workData.work_type === 1 || workData.work_type === 3) && workData.source_probs ? (
          <div className="space-y-4">
            {workData.source_probs.map((prob, index) => (
              <div key={index} className="rounded-3xl border border-gray-200 bg-gray-50 p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex-1">
                    <div className="text-sm text-gray-500">
                      라벨: {prob.labels || '없음'}
                    </div>
                    {prob.mathcepts && Object.keys(prob.mathcepts).length > 0 && (
                      <div className="mb-2">
                        <span className="text-xs font-medium text-gray-600">수학 개념:</span>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {Object.entries(prob.mathcepts).map(([id, concept]) => (
                            <span 
                              key={id} 
                              className="inline-block rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                              dangerouslySetInnerHTML={{ __html: renderLatex(concept) }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : workData.work_type === 2 && workData.source_cepts ? (
          <div>
            <h3 className="mb-3 text-sm font-medium text-gray-700">선택된 수학 개념</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(workData.source_cepts).map(([id, concept]) => (
                <span 
                  key={id} 
                  className="inline-block rounded-full bg-success/10 px-3 py-1 text-sm font-medium text-success"
                  dangerouslySetInnerHTML={{ __html: renderLatex(concept) }}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-gray-500">원본 정보가 없습니다.</p>
        )}
        
        {/* 문제 미리보기 - 원본 정보 섹션 내부 하단 */}
        {(workData.work_type === 1 || workData.work_type === 3) && workData.source_probs && workData.source_probs.length > 0 && (
          <div className="mt-2">
            <div className="problem-preview-wrapper">
              <ProblemPreviewCards
                problems={workData.source_probs.map((prob, _index) => ({
                  problem_id: prob.problem_id,
                  step: prob.step || 3, // 기본 난이도 3
                  address: prob.address || '',
                  labels: prob.labels
                }))}
                selectedTest={null}
                onViewProblem={(problemIds, _index, _isCorrect) => {
                  handleOriginalPreview(problemIds);
                }}
                isDesktop={false}
              />
              <style>{`
                .problem-preview-wrapper .px-4.py-\\[0px\\].bg-white {
                  padding-left: 1rem !important;
                  padding-right: 1rem !important;
                  background-color: transparent !important;
                }
                .problem-preview-wrapper .problem-number {
                  display: none !important;
                }
                .problem-preview-wrapper button[title="새 문제 AI 생성"] {
                  display: none !important;
                }
                .problem-preview-wrapper .shadow-lg {
                  box-shadow: none !important;
                }
                .problem-preview-wrapper .hover\\:shadow-2xl:hover {
                  box-shadow: none !important;
                }
                .problem-preview-wrapper .hover\\:scale-\\[1\\.02\\]:hover {
                  transform: none !important;
                }
                .problem-preview-wrapper .hover\\:border-primary\\/20:hover {
                  border-color: #e5e7eb !important;
                }
                .problem-preview-wrapper > div > div {
                  box-shadow: none !important;
                  cursor: default !important;
                }
              `}</style>
            </div>
          </div>
        )}
      </div>

      {/* 생성된 문제 섹션 */}
      {workData.work_result && (
        <div className="space-y-4">
          {/* 문제 섹션 - work_type이 3(문제 분석)이 아닌 경우에만 표시 */}
          {workData.work_type !== 3 && (
          <div className="rounded-3xl bg-white p-4 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">🎯 생성된 문제</h2>
              <button
                onClick={() => setIsEditingProblem(!isEditingProblem)}
                className={`rounded-3xl px-4 py-2 text-sm font-medium transition-colors ${
                  isEditingProblem 
                    ? 'bg-gray-400 text-white hover:bg-gray-500' 
                    : 'bg-primary text-white hover:bg-primary-hover'
                }`}
              >
                {isEditingProblem ? '편집 취소' : '문제 편집'}
              </button>
            </div>
            
            <div className="rounded-lg border border-gray-200 p-4">
              {isEditingProblem ? (
                <div className="[&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
                  <EditorComposer initialEditorState={getProblemInitialEditorState()}>
                    <EditorComponent 
                      onLoad={setProblemMathDataHandler}
                      onSave={handleProblemSave}
                      editorKey="problem-editor"
                    />
                  </EditorComposer>
                </div>
              ) : (
                <div className="[&_.problem-content]:!font-inherit [&_.problem-content_*:not(.katex):not(.katex_*)]:!font-[Pretendard] [&_.problem-content_*:not(.katex):not(.katex_*)]:!text-lg [&_.problem-content_*:not(.katex):not(.katex_*)]:!font-medium">
                  <LexicalRenderer lexicalData={workData.work_result.prob_lexical} />
                </div>
              )}
            </div>
          </div>
          )}

          {/* 추가 정보 섹션 */}
          <div className="rounded-3xl bg-white p-4 shadow-lg">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">📊 분석 결과</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setIsEditingPrompt(!isEditingPrompt);
                    if (!isEditingPrompt) {
                      setEditPrompt(workData.user_prompt);
                    }
                  }}
                  className={`flex items-center gap-2 rounded-3xl px-4 py-2 text-sm font-medium transition-colors ${
                    isEditingPrompt 
                      ? 'bg-gray-400 text-white hover:bg-gray-500' 
                      : 'bg-primary text-white hover:bg-primary-hover'
                  }`}
                >
                  {!isEditingPrompt && (
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  )}
                  {isEditingPrompt ? '편집 취소' : '프롬프트 변경'}
                </button>
                {canRetry && (
                  <button
                    onClick={handleRetry}
                    className="flex items-center gap-2 rounded-3xl bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                  >
                    <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    재시도
                  </button>
                )}
              </div>
            </div>

            {/* 분석 옵션 편집 (work_type=3일 때만) */}
            {workData.work_type === 3 && (
              <div className="rounded-3xl border border-gray-200 p-4">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-semibold flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    분석 옵션
                  </h2>
                  <button
                    onClick={() => {
                      setIsEditingOptions(!isEditingOptions);
                      if (!isEditingOptions) {
                        // 편집 모드 진입 시 현재 옵션으로 초기화
                        if (workData.options) {
                          setAnalysisOptions({
                            solution: workData.options.solution || false,
                            answer: workData.options.answer || false,
                            concept_ids: workData.options.concept_ids || false,
                            difficulty: workData.options.difficulty || false
                          });
                        }
                      }
                    }}
                    className={`rounded-3xl px-3 text-sm font-medium transition-colors ${
                      isEditingOptions 
                        ? 'bg-gray-400 text-white hover:bg-gray-500' 
                        : 'bg-primary text-white hover:bg-primary-hover'
                    }`}
                    style={{ height: '32px' }}
                  >
                    {isEditingOptions ? '취소' : '옵션 편집'}
                  </button>
                </div>

                {isEditingOptions ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { key: 'solution', label: '풀이' },
                        { key: 'answer', label: '정답' },
                        { key: 'concept_ids', label: '개념 ID' },
                        { key: 'difficulty', label: '난이도' }
                      ].map(option => (
                        <label key={option.key} className="flex cursor-pointer items-center gap-2 rounded border p-2">
                          <input
                            type="checkbox"
                            checked={analysisOptions[option.key]}
                            onChange={(e) => 
                              setAnalysisOptions(prev => ({
                                ...prev,
                                [option.key]: e.target.checked
                              }))
                            }
                            className="rounded text-primary focus:ring-primary"
                          />
                          <span className="text-sm">{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleOptionsSave}
                        className="rounded-3xl bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                        style={{ height: '32px' }}
                      >
                        저장
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingOptions(false);
                          // 원래 상태로 복원
                          if (workData.options) {
                            setAnalysisOptions({
                              solution: workData.options.solution || false,
                              answer: workData.options.answer || false,
                              concept_ids: workData.options.concept_ids || false,
                              difficulty: workData.options.difficulty || false
                            });
                          }
                        }}
                        className="rounded-3xl bg-gray-400 px-3 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                        style={{ height: '32px' }}
                      >
                        취소
                      </button>
                    </div>
                    <div className="text-xs text-gray-500">
                      * 최소 하나 이상 선택해주세요
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">
                    {workData.options ? (
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(workData.options).map(([key, value]) => 
                          value && (
                            <span key={key} className="inline-flex items-center rounded-full bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                              {key === 'solution' ? '풀이' : 
                               key === 'answer' ? '정답' : 
                               key === 'concept_ids' ? '개념 ID' : 
                               key === 'difficulty' ? '난이도' : key}
                            </span>
                          )
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-500">분석 옵션이 설정되지 않았습니다</span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* 프롬프트 편집 UI */}
            {isEditingPrompt && (
              <div className="mb-4 space-y-3 rounded-3xl border border-gray-200 p-4">
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  className="w-full rounded-3xl border p-3 focus:border-orange-500 focus:ring-orange-500"
                  rows="4"
                  maxLength="500"
                  placeholder="AI 지시사항을 입력하세요 (최대 500자)"
                />
                <div className="text-right text-xs text-gray-500">
                  {(editPrompt?.length || 0)}/500
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handlePromptRetry}
                    className="rounded-3xl bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                  >
                    변경 후 재시도
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPrompt(false);
                      setEditPrompt(workData.user_prompt);
                    }}
                    className="rounded-3xl bg-gray-400 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                  >
                    취소
                  </button>
                </div>
              </div>
            )}
            
            {/* 적용된 수학 개념 */}
            {workData.work_result.mathcepts && Object.keys(workData.work_result.mathcepts).length > 0 && (
              <div className="mb-4 rounded-3xl border border-gray-200 p-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                적용된 수학 개념
              </h2>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(workData.work_result.mathcepts).map(([id, concept]) => {
                    const matched = matchedConcepts[id];
                    
                    const getConceptTypeColor = (type) => {
                      const colors = {
                        'DEF': 'rgba(239, 68, 68, 0.5)',   // 빨강 (정의)
                        'AXM': 'rgba(249, 115, 22, 0.5)',  // 주황 (공리)
                        'IDN': 'rgba(245, 158, 11, 0.5)',  // 노랑 (정리)
                        'THM': 'rgba(34, 197, 94, 0.5)',   // 초록 (이론)
                        'MTH': 'rgba(6, 182, 212, 0.5)',   // 하늘 (방법론)
                        'PRP': 'rgba(59, 130, 246, 0.5)',  // 파랑 (성질)
                        'FOR': 'rgba(139, 92, 246, 0.5)',  // 보라 (공식)
                        'NTN': 'rgba(236, 72, 153, 0.5)'   // 분홍 (표기법)
                      };
                      return colors[type] || 'rgba(251, 146, 60, 0.5)';
                    };
                    
                    return (
                      <div 
                        key={id} 
                        className="flex items-center gap-2 rounded-3xl border border-gray-200 px-3 py-1 text-xs text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => fetchMathceptDetails(id)}
                        title="클릭하여 상세 정보 보기"
                      >
                        {matched?.order && (
                          <span 
                            className="inline-flex items-center justify-center font-semibold text-gray-700"
                            style={{
                              backgroundColor: 'rgba(156, 163, 175, 0.3)',
                              borderRadius: '6px',
                              width: '32px',
                              height: '20px',
                              minWidth: '32px',
                              fontSize: '10px'
                            }}
                          >
                            {matched.order}
                          </span>
                        )}
                        
                        {matched?.concept_type && (
                          <span 
                            className="inline-flex items-center justify-center font-semibold text-gray-800"
                            style={{
                              backgroundColor: getConceptTypeColor(matched.concept_type),
                              borderRadius: '6px',
                              width: '32px',
                              height: '20px',
                              minWidth: '32px',
                              fontSize: '9px'
                            }}
                          >
                            {matched.concept_type}
                          </span>
                        )}
                        
                        <span dangerouslySetInnerHTML={{ __html: renderLatex(concept) }} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* 정답 및 난이도 - 가로 배치 (모바일: 세로) */}
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">
              {/* 정답 편집 */}
              <div className="flex-1 rounded-3xl border border-gray-200 p-4">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        정답
                      </h2>
                      <button
                        onClick={() => {
                          setIsEditingAnswer(!isEditingAnswer);
                          if (!isEditingAnswer) {
                            setEditAnswer(workData.work_result.answer || '');
                          }
                        }}
                        className={`rounded-3xl px-3 text-sm font-medium transition-colors ${
                          isEditingAnswer 
                            ? 'bg-gray-400 text-white hover:bg-gray-500' 
                            : 'bg-primary text-white hover:bg-primary-hover'
                        }`}
                        style={{ height: '32px' }}
                      >
                        {isEditingAnswer ? '취소' : '정답 편집'}
                      </button>
                    </div>
                    {isEditingAnswer ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editAnswer}
                          onChange={(e) => setEditAnswer(e.target.value)}
                          className="w-full rounded-lg border border-gray-200 p-2 text-sm focus:border-primary focus:ring-primary"
                          placeholder="정답을 입력하세요 (LaTeX 형식 가능)"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleAnswerSave}
                            className="rounded-3xl bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                            style={{ height: '32px' }}
                          >
                            저장
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingAnswer(false);
                              setEditAnswer(workData.work_result.answer || '');
                            }}
                            className="rounded-3xl bg-gray-400 px-3 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                            style={{ height: '32px' }}
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-base font-medium text-gray-600">기존:</span>
                        {originalAnswer ? (
                          <span 
                            className="text-base text-gray-700"
                            dangerouslySetInnerHTML={{ __html: renderAnswerLatex(originalAnswer) }}
                          />
                        ) : (
                          <span className="text-base text-gray-500">정답 없음</span>
                        )}
                        <span className="text-base text-gray-500 mx-2">→</span>
                        <span className="text-base font-medium text-blue-600">분석:</span>
                        {workData.work_result.answer ? (
                          <span 
                            className="text-base text-blue-700"
                            dangerouslySetInnerHTML={{ __html: renderAnswerLatex(workData.work_result.answer) }}
                          />
                        ) : (
                          <span className="text-base text-gray-500">정답이 없습니다</span>
                        )}
                      </div>
                    )}
              </div>

              {/* 난이도 편집 */}
              <div className="flex-1 rounded-3xl border border-gray-200 p-4">
                    <div className="mb-4 flex items-center justify-between">
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" />
                        난이도
                      </h2>
                      <button
                        onClick={() => {
                          setIsEditingDifficulty(!isEditingDifficulty);
                          if (!isEditingDifficulty) {
                            setEditDifficulty(workData.work_result.difficulty || 1);
                          }
                        }}
                        className={`rounded-3xl px-3 text-sm font-medium transition-colors ${
                          isEditingDifficulty 
                            ? 'bg-gray-400 text-white hover:bg-gray-500' 
                            : 'bg-primary text-white hover:bg-primary-hover'
                        }`}
                        style={{ height: '32px' }}
                      >
                        {isEditingDifficulty ? '취소' : '난이도 편집'}
                      </button>
                    </div>
                    {isEditingDifficulty ? (
                      <div className="space-y-2">
                        <select
                          value={editDifficulty}
                          onChange={(e) => setEditDifficulty(parseInt(e.target.value))}
                          className="w-full rounded-lg border border-gray-200 p-2 text-sm focus:border-primary focus:ring-primary"
                        >
                          <option value={1}>1 (매우 쉬움)</option>
                          <option value={2}>2 (쉬움)</option>
                          <option value={3}>3 (보통)</option>
                          <option value={4}>4 (어려움)</option>
                          <option value={5}>5 (매우 어려움)</option>
                        </select>
                        <div className="flex gap-2">
                          <button
                            onClick={handleDifficultySave}
                            className="rounded-3xl bg-primary px-3 text-sm font-medium text-white transition-colors hover:bg-primary-hover"
                            style={{ height: '32px' }}
                          >
                            저장
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingDifficulty(false);
                              setEditDifficulty(workData.work_result.difficulty || 1);
                            }}
                            className="rounded-3xl bg-gray-400 px-3 text-sm font-medium text-white transition-colors hover:bg-gray-500"
                            style={{ height: '32px' }}
                          >
                            취소
                          </button>
                        </div>
                      </div>
                    ) : (
                      workData.work_result.difficulty ? (
                        <span className="text-base">
                          {'★'.repeat(workData.work_result.difficulty)}
                          {'☆'.repeat(5 - workData.work_result.difficulty)}
                          <span className="ml-2 text-sm text-gray-500">({workData.work_result.difficulty}/5)</span>
                        </span>
                      ) : (
                        <span className="text-base text-gray-500">난이도가 설정되지 않았습니다</span>
                      )
                    )}
              </div>
            </div>
            
            {/* 풀이 섹션 - AI 코멘트 위로 이동 */}
            <div className="mt-4 rounded-3xl border border-gray-200 p-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                  <Lightbulb className="w-5 h-5" />
                  {workData.work_type === 3 ? '분석된 풀이' : '생성된 풀이'}
                </h2>
                <button
                  onClick={() => setIsEditingSolution(!isEditingSolution)}
                  className={`rounded-3xl px-4 py-2 text-sm font-medium transition-colors ${
                    isEditingSolution 
                      ? 'bg-gray-400 text-white hover:bg-gray-500' 
                      : 'bg-primary text-white hover:bg-primary-hover'
                  }`}
                >
                  {isEditingSolution ? '편집 취소' : '풀이 편집'}
                </button>
              </div>
              
              <div className="rounded-lg">
                {isEditingSolution ? (
                  <div className="[&_.editor-container]:!font-inherit [&_div.editor-equation]:block [&_span.editor-equation]:inline-block">
                    <EditorComposer initialEditorState={getSolutionInitialEditorState()}>
                      <EditorComponent 
                        onLoad={setSolutionMathDataHandler}
                        onSave={handleSolutionSave}
                        editorKey="solution-editor"
                      />
                    </EditorComposer>
                  </div>
                ) : (
                  <div className="[&_.problem-content]:!font-inherit [&_.problem-content_*:not(.katex):not(.katex_*)]:!font-[Pretendard] [&_.problem-content_*:not(.katex):not(.katex_*)]:!text-lg [&_.problem-content_*:not(.katex):not(.katex_*)]:!font-medium">
                    <LexicalRenderer lexicalData={workData.work_result.sol_lexical} />
                  </div>
                )}
              </div>
            </div>
            
            {/* AI 코멘트 - 하단 한 줄 */}
            {workData.work_result.description && (
              <div className="mt-4 rounded-3xl border border-gray-200 p-4">
                <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                AI 코멘트
              </h2>
                <p 
                  className="mt-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-900"
                  dangerouslySetInnerHTML={{ __html: renderLatex(workData.work_result.description) }}
                />
              </div>
            )}
            
            {/* 하단 액션 버튼들 - 추가정보 섹션 내부 */}
            {canComplete && (
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={navigateToList}
                  className="rounded-3xl bg-gray-400 px-6 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-gray-500"
                >
                  취소
                </button>
                <button
                  onClick={handleComplete}
                  className="flex items-center gap-2 rounded-3xl bg-primary px-6 py-3 text-sm font-medium text-white shadow-lg transition-colors hover:bg-primary-hover"
                >
                  <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  문제 생성 완료
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ProblemPreview 컴포넌트 */}
      <ProblemPreview
        isOpen={previewInfo.isOpen}
        onClose={() => setPreviewInfo({
          isOpen: false,
          problemId: [],
          problemNumber: null,
          isCorrect: null
        })}
        problemIds={previewInfo.problemId}
        showNumbers={false}
        correctList={[]}
        problemNumbers={[]}
      />

      {/* 매스컨셉 상세 정보 모달 - 공통 컴포넌트 사용 (닫기 버튼 없음) */}
      <MathceptDetailModal
        isOpen={mathceptDetailModalOpen}
        onClose={closeMathceptDetailModal}
        data={mathceptDetailData}
        loading={mathceptDetailLoading}
        renderLatex={renderLatex}
        showCloseButton={false}
        useLoader={false}
        handleAuth={false}
      />

      {/* 글로벌 스타일 */}
      <style jsx global>{`
        .problem-content *:not(.katex):not(.katex *) {
          font-family: 'Pretendard', sans-serif !important;
          font-weight: 500 !important;
          font-size: 18px !important;
        }
        .katex {
          font-size: 1em !important;
        }
        .PlaygroundEditorTheme__boxContent {
          border: 1px solid black;
          padding: 10px;
          margin: 10px auto;
          max-width: 100%;
          border-radius: 4px;
          background-color: #ffffff;
        }
      `}</style>
    </div>
  );
}

export default WorkDetail; 